// Colophon — shared end-of-story footer.
//
// Extracted from public/src/components/viewer/Colophon.tsx into
// @scrolly/viewer so both app/ and public/ consumers can render the
// canonical "End." footer via the renderFooter slot.
//
// Two visual variants:
//   - ColophonSubtle (default): newspaper back matter, BG #f7f5ef
//   - ColophonBold: magazine back cover, BG #0a0a0a, Mega-"End." in Pink

import { MarkdownViewer } from "@scrolly/renderer";
import { t, type Lang } from "./i18n";

const PINK = "#FF3366";

export type ColophonStyle = "subtle" | "bold";

export interface ColophonLegalLinks {
  imprintUrl?: string;
  privacyUrl?: string;
  accessibilityUrl?: string;
  address?: string;
}

export interface ColophonOrgContact {
  name: string;
  email?: string;
}

export interface ColophonProps {
  creatorName: string;
  publishedAt?: string;
  duration?: string;
  views?: string;
  credits?: string;
  sources?: string;
  contact?: string;
  orgContact?: ColophonOrgContact | null;
  legalLinks?: ColophonLegalLinks | null;
  wide?: boolean;
  isCompact?: boolean;
  style?: ColophonStyle;
  lang?: Lang;
}

interface SourceEntry {
  label: string;
  url: string;
}

const SOURCES_SEPARATOR = " • ";

function parseSourcesMarkdown(sourcesMd: string | undefined): SourceEntry[] {
  if (!sourcesMd || typeof sourcesMd !== "string") return [];
  const raw = sourcesMd.split(SOURCES_SEPARATOR);
  return raw
    .map((part) => {
      const match = part.match(/\[(.*?)\]\((.*?)\)/);
      if (!match) return null;
      const label = match[1]?.trim();
      const url = match[2]?.trim();
      if (!label || !url) return null;
      return { label, url };
    })
    .filter((x): x is SourceEntry => x !== null);
}

export function Colophon({
  creatorName,
  publishedAt,
  duration,
  views,
  credits,
  sources: sourcesMd,
  contact: contactMd,
  orgContact,
  legalLinks,
  wide = false,
  isCompact = false,
  style = "subtle",
  lang = 'de',
}: ColophonProps) {
  const parsedSources = parseSourcesMarkdown(sourcesMd);
  const creditsTrimmed = credits?.trim() ?? "";
  const contactTrimmed = contactMd?.trim() ?? "";
  const published = publishedAt || "";

  const narrow = isCompact || !wide;

  if (style === "bold") {
    return (
      <ColophonBold
        creatorName={creatorName}
        wide={wide}
        narrow={narrow}
        sources={parsedSources}
        creditsMd={creditsTrimmed}
        contactMd={contactTrimmed}
        published={published}
        duration={duration}
        views={views}
        orgContact={orgContact}
        legalLinks={legalLinks}
        lang={lang}
      />
    );
  }
  return (
    <ColophonSubtle
      creatorName={creatorName}
      wide={wide}
      narrow={narrow}
      sources={parsedSources}
      creditsMd={creditsTrimmed}
      contactMd={contactTrimmed}
      published={published}
      duration={duration}
      views={views}
      orgContact={orgContact}
      legalLinks={legalLinks}
      lang={lang}
    />
  );
}

interface SharedSubProps {
  creatorName: string;
  wide: boolean;
  narrow: boolean;
  sources: SourceEntry[];
  creditsMd: string;
  contactMd: string;
  published: string;
  duration?: string;
  views?: string;
  orgContact?: ColophonOrgContact | null;
  legalLinks?: ColophonLegalLinks | null;
  lang: Lang;
}

function ColophonSubtle({
  creatorName,
  wide,
  narrow,
  sources,
  creditsMd,
  contactMd,
  published,
  duration,
  views,
  orgContact,
  legalLinks,
  lang,
}: SharedSubProps) {
  const p = wide && !narrow ? "100px 60px 60px" : "60px 24px 36px";
  const grid2Col = wide && !narrow ? "140px 1fr" : "1fr";
  const longStringStyle: React.CSSProperties = narrow
    ? { overflowWrap: "anywhere", wordBreak: "break-word" }
    : {};
  const hasCredits = creditsMd.length > 0;
  const hasSources = sources.length > 0;
  const address = legalLinks?.address;
  const hasContactSection =
    !!orgContact?.name || !!orgContact?.email || contactMd.length > 0;

  return (
    <div
      data-testid="colophon-subtle"
      style={{
        background: "#f7f5ef",
        color: "#1a1a1a",
        padding: p,
        fontFamily: "inherit",
      }}
    >
      <div
        style={{
          maxWidth: wide ? 820 : "100%",
          margin: wide ? "0 auto" : 0,
        }}
      >
        <div
          style={{
            marginBottom: wide ? 56 : 40,
            display: "flex",
            justifyContent: "flex-start",
          }}
        >
          <div
            style={{
              width: 48,
              height: 3,
              borderRadius: 1.5,
              background: "#d4d0c8",
            }}
          />
        </div>

        {/* Byline */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: grid2Col,
            gap: narrow ? 20 : 40,
            paddingBottom: narrow ? 24 : 32,
            borderBottom: "1px solid #e3e0d5",
          }}
        >
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 600,
              letterSpacing: 1.4,
              textTransform: "uppercase",
              color: "#7a766b",
              paddingTop: 4,
            }}
          >
            {t('colophon.by', lang)}
          </div>
          <div>
            <div
              style={{
                fontFamily: '"Instrument Serif", Georgia, serif',
                fontSize: narrow ? 24 : 34,
                letterSpacing: -0.4,
                lineHeight: 1.1,
                ...longStringStyle,
              }}
            >
              {creatorName}
            </div>
            <div
              style={{
                fontSize: 13,
                color: "#6b6760",
                marginTop: 6,
              }}
            >
              {published ? <>{t('colophon.published', lang)} {published}</> : null}
              {published && duration ? " · " : null}
              {duration || null}
              {(published || duration) && views ? " · " : null}
              {views ? `${views} ${t('colophon.scrolls', lang)}` : null}
            </div>
          </div>
        </div>

        {/* Credits */}
        {hasCredits && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: grid2Col,
              gap: narrow ? 20 : 40,
              padding: narrow ? "24px 0" : "32px 0",
              borderBottom: "1px solid #e3e0d5",
            }}
          >
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: "#7a766b",
                paddingTop: 4,
              }}
            >
              {t('colophon.credits', lang)}
            </div>
            <div
              className="colophon-markdown"
              style={{
                fontSize: 14,
                lineHeight: 1.55,
                color: "#1a1a1a",
                ...longStringStyle,
              }}
            >
              <MarkdownViewer source={creditsMd} />
            </div>
          </div>
        )}

        {/* Sources */}
        {hasSources && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: grid2Col,
              gap: narrow ? 20 : 40,
              padding: narrow ? "24px 0" : "32px 0",
              borderBottom: "1px solid #e3e0d5",
            }}
          >
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: "#7a766b",
                paddingTop: 4,
              }}
            >
              {t('colophon.sources', lang)}
            </div>
            <ol
              style={{
                margin: 0,
                padding: 0,
                listStyle: "none",
                display: "flex",
                flexDirection: "column",
                gap: 10,
              }}
            >
              {sources.map((s, i) => (
                <li
                  key={i}
                  style={{
                    display: "flex",
                    gap: 14,
                    fontSize: 14,
                    lineHeight: 1.5,
                  }}
                >
                  <span
                    style={{
                      fontFamily: '"Instrument Serif", Georgia, serif',
                      fontSize: 15,
                      color: PINK,
                      fontStyle: "italic",
                      width: 20,
                      flexShrink: 0,
                    }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span style={{ minWidth: 0, ...longStringStyle }}>
                    <a
                      href={s.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "#1a1a1a",
                        textDecoration: "none",
                        borderBottom: "1px solid transparent",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.borderBottomColor = "currentColor")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.borderBottomColor = "transparent")
                      }
                    >
                      {s.label}
                    </a>
                    <span style={{ color: "#9a968a", marginLeft: 8 }}>
                      {s.url}
                    </span>
                  </span>
                </li>
              ))}
            </ol>
          </div>
        )}

        {/* Contact */}
        {hasContactSection && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: grid2Col,
              gap: narrow ? 20 : 40,
              padding: narrow ? "24px 0" : "32px 0",
              borderBottom: "1px solid #e3e0d5",
            }}
          >
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: "#7a766b",
                paddingTop: 4,
              }}
            >
              {t('colophon.contact', lang)}
            </div>
            <div
              style={{
                fontSize: 14,
                lineHeight: 1.55,
                color: "#1a1a1a",
                minWidth: 0,
                ...longStringStyle,
              }}
            >
              {orgContact?.name && (
                <div style={{ marginBottom: 6, ...longStringStyle }}>
                  <strong>{orgContact.name}</strong>
                </div>
              )}
              {address && (
                <div
                  style={{
                    color: "#6b6760",
                    marginBottom: 6,
                    ...longStringStyle,
                  }}
                >
                  {address.split(",").map((part, i, arr) => (
                    <span key={i}>
                      {part.trim()}
                      {i < arr.length - 1 && <br />}
                    </span>
                  ))}
                </div>
              )}
              {orgContact?.email && (
                <div
                  style={{
                    color: "#6b6760",
                    marginBottom: 6,
                    ...longStringStyle,
                  }}
                >
                  <a
                    href={`mailto:${orgContact.email}`}
                    style={{
                      color: "#1a1a1a",
                      textDecoration: "underline",
                      ...longStringStyle,
                    }}
                  >
                    {orgContact.email}
                  </a>
                </div>
              )}
              {!orgContact?.name && contactMd && (
                <div className="colophon-markdown" style={longStringStyle}>
                  <MarkdownViewer source={contactMd} />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Abbinder */}
        <div
          style={{
            marginTop: narrow ? 56 : 80,
            paddingTop: 24,
            borderTop: "1px solid #e3e0d5",
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 16,
          }}
        >
          <div style={{ fontSize: 13, color: "#1a1a1a" }}>
            <span
              style={{
                fontFamily: '"Instrument Serif", Georgia, serif',
                fontStyle: "italic",
                fontSize: 17,
                marginRight: 6,
              }}
            >
              {t('colophon.scrolledOn', lang)}
            </span>
            <span style={{ fontWeight: 600, letterSpacing: -0.2 }}>Scrolly</span>
            <span style={{ color: "#9a968a", marginLeft: 10 }}>
              {t('colophon.publishOwn', lang)}{" "}
              <a
                href="https://scrolly.online"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "#1a1a1a",
                  borderBottom: "1px solid currentColor",
                  textDecoration: "none",
                }}
              >
                scrolly.online
              </a>
            </span>
          </div>
          <AbbinderLinks legalLinks={legalLinks} color="#9a968a" hoverColor="#1a1a1a" lang={lang} />
        </div>
      </div>
    </div>
  );
}

function AbbinderLinks({
  legalLinks,
  color,
  hoverColor,
  lang,
}: {
  legalLinks?: ColophonLegalLinks | null;
  color: string;
  hoverColor: string;
  lang: Lang;
}) {
  const linkStyle: React.CSSProperties = {
    color,
    textDecoration: "none",
    transition: "color 120ms ease-out",
  };
  return (
    <div
      style={{
        display: "flex",
        gap: 14,
        fontSize: 11.5,
        color,
      }}
    >
      {legalLinks?.imprintUrl ? (
        <a
          href={legalLinks.imprintUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={linkStyle}
          onMouseEnter={(e) => (e.currentTarget.style.color = hoverColor)}
          onMouseLeave={(e) => (e.currentTarget.style.color = color)}
        >
          {t('colophon.imprint', lang)}
        </a>
      ) : (
        <span>{t('colophon.imprint', lang)}</span>
      )}
      {legalLinks?.privacyUrl ? (
        <a
          href={legalLinks.privacyUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={linkStyle}
          onMouseEnter={(e) => (e.currentTarget.style.color = hoverColor)}
          onMouseLeave={(e) => (e.currentTarget.style.color = color)}
        >
          {t('colophon.privacy', lang)}
        </a>
      ) : (
        <span>{t('colophon.privacy', lang)}</span>
      )}
      <span>{t('colophon.terms', lang)}</span>
      <span>© 2026</span>
    </div>
  );
}

function ColophonBold({
  creatorName,
  narrow,
  sources,
  creditsMd,
  contactMd,
  published,
  duration,
  views,
  orgContact,
  legalLinks,
  lang,
}: SharedSubProps) {
  const p = narrow ? "72px 24px 36px" : "120px 60px 56px";
  const longStringStyle: React.CSSProperties = narrow
    ? { overflowWrap: "anywhere", wordBreak: "break-word" }
    : {};
  const hasCredits = creditsMd.length > 0;
  const hasSources = sources.length > 0;
  const address = legalLinks?.address;
  const hasContact =
    !!orgContact?.name || !!orgContact?.email || contactMd.length > 0;

  return (
    <div
      data-testid="colophon-bold"
      style={{
        background: "#0a0a0a",
        color: "#fff",
        padding: p,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Separator line (replaces "End." word — no language problem) */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          margin: narrow ? "0 0 50px" : "0 0 80px",
        }}
      >
        <div
          style={{
            width: 64,
            height: 4,
            borderRadius: 2,
            background: PINK,
          }}
        />
      </div>

      <div style={{ maxWidth: narrow ? "100%" : 960, margin: "0 auto" }}>
        {/* Two-column editorial block */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: !narrow && hasCredits ? "1fr 1fr" : "1fr",
            gap: narrow ? 36 : 60,
            paddingBottom: narrow ? 32 : 48,
            borderBottom: "1px solid rgba(255,255,255,0.12)",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: PINK,
                marginBottom: 14,
              }}
            >
              {t('colophon.theByline', lang)}
            </div>
            <div
              style={{
                fontFamily: '"Instrument Serif", Georgia, serif',
                fontSize: narrow ? 30 : 44,
                letterSpacing: -0.8,
                lineHeight: 1.05,
                ...longStringStyle,
              }}
            >
              {creatorName}
            </div>
            <div
              style={{
                fontSize: 13.5,
                color: "rgba(255,255,255,0.55)",
                marginTop: 12,
                lineHeight: 1.55,
              }}
            >
              {published ? <>{t('colophon.published', lang)} {published}</> : null}
              {published && duration ? " · " : null}
              {duration || null}
              {views ? (
                <>
                  <br />
                  {t('colophon.readBy', lang)} {views} {t('colophon.people', lang)}
                </>
              ) : null}
            </div>
          </div>
          {hasCredits && (
            <div>
              <div
                style={{
                  fontSize: 10.5,
                  fontWeight: 600,
                  letterSpacing: 1.4,
                  textTransform: "uppercase",
                  color: PINK,
                  marginBottom: 14,
                }}
              >
                {t('colophon.credits', lang)}
              </div>
              <div
                className="colophon-markdown"
                style={{
                  fontSize: 14.5,
                  lineHeight: 1.55,
                  color: "rgba(255,255,255,0.92)",
                }}
              >
                <MarkdownViewer source={creditsMd} />
              </div>
            </div>
          )}
        </div>

        {/* Sources */}
        {hasSources && (
          <div
            style={{
              padding: narrow ? "32px 0" : "48px 0",
              borderBottom: "1px solid rgba(255,255,255,0.12)",
            }}
          >
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: PINK,
                marginBottom: 18,
              }}
            >
              {t('colophon.sources', lang)}
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: narrow ? "1fr" : "repeat(3, 1fr)",
                gap: narrow ? 18 : 32,
              }}
            >
              {sources.map((s, i) => (
                <div
                  key={i}
                  style={{
                    borderTop: "1px solid rgba(255,255,255,0.15)",
                    paddingTop: 12,
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      fontFamily: '"Instrument Serif", Georgia, serif',
                      fontSize: 22,
                      color: PINK,
                      fontStyle: "italic",
                      lineHeight: 1,
                    }}
                  >
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: "block",
                      fontSize: 14,
                      color: "#fff",
                      marginTop: 8,
                      lineHeight: 1.35,
                      textDecoration: "none",
                      ...longStringStyle,
                    }}
                  >
                    {s.label}
                  </a>
                  <div
                    style={{
                      fontSize: 12,
                      color: "rgba(255,255,255,0.45)",
                      marginTop: 4,
                      ...longStringStyle,
                    }}
                  >
                    {s.url}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Contact */}
        {hasContact && (
          <div
            style={{
              padding: narrow ? "28px 0" : "40px 0",
              borderBottom: "1px solid rgba(255,255,255,0.12)",
              fontSize: 14,
              lineHeight: 1.55,
              color: "rgba(255,255,255,0.85)",
            }}
          >
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: PINK,
                marginBottom: 14,
              }}
            >
              {t('colophon.contact', lang)}
            </div>
            {orgContact?.name && (
              <div style={{ color: "#fff", marginBottom: 6, ...longStringStyle }}>
                <strong>{orgContact.name}</strong>
              </div>
            )}
            {address && (
              <div
                style={{
                  color: "rgba(255,255,255,0.55)",
                  marginBottom: 6,
                  ...longStringStyle,
                }}
              >
                {address.split(",").map((part, i, arr) => (
                  <span key={i}>
                    {part.trim()}
                    {i < arr.length - 1 && <br />}
                  </span>
                ))}
              </div>
            )}
            {orgContact?.email && (
              <div
                style={{
                  color: "rgba(255,255,255,0.55)",
                  marginBottom: 6,
                  ...longStringStyle,
                }}
              >
                <a
                  href={`mailto:${orgContact.email}`}
                  style={{
                    color: "#fff",
                    textDecoration: "underline",
                    ...longStringStyle,
                  }}
                >
                  {orgContact.email}
                </a>
              </div>
            )}
            {!orgContact?.name && contactMd && (
              <div className="colophon-markdown" style={longStringStyle}>
                <MarkdownViewer source={contactMd} />
              </div>
            )}
          </div>
        )}

        {/* Abbinder as pull quote */}
        <div
          style={{
            padding: narrow ? "44px 0 16px" : "64px 0 16px",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontFamily: '"Instrument Serif", Georgia, serif',
              fontSize: narrow ? 24 : 36,
              lineHeight: 1.2,
              letterSpacing: -0.4,
              fontStyle: "italic",
              color: "rgba(255,255,255,0.9)",
            }}
          >
            {t('colophon.scrolledOn', lang)}{" "}
            <span
              style={{
                color: PINK,
                fontStyle: "normal",
                fontFamily: "inherit",
                fontWeight: 500,
                letterSpacing: -0.8,
              }}
            >
              Scrolly
            </span>
            .
          </div>
          <div
            style={{
              marginTop: 14,
              fontSize: 13,
              color: "rgba(255,255,255,0.5)",
              letterSpacing: 0.3,
            }}
          >
            {t('colophon.publishOwnStory', lang)}{" "}
            <a
              href="https://scrolly.online"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: "#fff",
                borderBottom: "1px solid rgba(255,255,255,0.3)",
                textDecoration: "none",
              }}
            >
              scrolly.online
            </a>
          </div>
          <BoldAbbinderLinks legalLinks={legalLinks} wide={!narrow} lang={lang} />
        </div>
      </div>
    </div>
  );
}

function BoldAbbinderLinks({
  legalLinks,
  wide,
  lang,
}: {
  legalLinks?: ColophonLegalLinks | null;
  wide: boolean;
  lang: Lang;
}) {
  const linkBase: React.CSSProperties = {
    color: "rgba(255,255,255,0.55)",
    textDecoration: "none",
    transition: "color 120ms ease-out",
  };
  const linkHover = "#fff";
  return (
    <div
      style={{
        marginTop: wide ? 40 : 28,
        display: "flex",
        gap: 14,
        justifyContent: "center",
        flexWrap: "wrap",
        fontSize: 11,
        color: "rgba(255,255,255,0.35)",
        letterSpacing: 0.4,
      }}
    >
      {legalLinks?.imprintUrl ? (
        <a
          href={legalLinks.imprintUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={linkBase}
          onMouseEnter={(e) => (e.currentTarget.style.color = linkHover)}
          onMouseLeave={(e) =>
            (e.currentTarget.style.color = linkBase.color as string)
          }
        >
          {t('colophon.imprint', lang)}
        </a>
      ) : (
        <span>{t('colophon.imprint', lang)}</span>
      )}
      <span>·</span>
      {legalLinks?.privacyUrl ? (
        <a
          href={legalLinks.privacyUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={linkBase}
          onMouseEnter={(e) => (e.currentTarget.style.color = linkHover)}
          onMouseLeave={(e) =>
            (e.currentTarget.style.color = linkBase.color as string)
          }
        >
          {t('colophon.privacy', lang)}
        </a>
      ) : (
        <span>{t('colophon.privacy', lang)}</span>
      )}
      <span>·</span>
      <span>{t('colophon.terms', lang)}</span>
      <span>·</span>
      <span>© 2026 Scrolly</span>
    </div>
  );
}

export default Colophon;
